import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { startLiveReload } from './lib/live-reload.ts';
import { AnalyticsIdentity } from './observability/identity.tsx';
import { reportError, startAnalytics } from './observability/posthog.ts';
import { routeTree } from './routeTree.gen';
import './styles.css';

// Before anything below can throw. Attaching the global listeners is
// synchronous; fetching the key is not, and deliberately does not block render.
startAnalytics();

// Preview stands only, and only because their index.html says so — it is a
// no-op anywhere else, production included (docs/STATIC.md).
startLiveReload();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('#root is missing from index.html');

// React swallows render errors it can recover from, and the window 'error'
// listener never sees them. These two hooks are React 19's supported way to
// observe both halves: what no boundary caught, and what one did.
createRoot(rootElement, {
  onUncaughtError: (error) => reportError(error),
  onCaughtError: (error) => reportError(error),
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AnalyticsIdentity />
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
